import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { COLORS, LAYOUT } from '../../constants';

// Package Icon
const PackageIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

// Shopping Bag Icon
const ShoppingBagIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

// Dollar Icon
const DollarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

// Star Icon
const StarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ProfileStats = ({ userData }) => {
  const [stats, setStats] = useState({
    totalListings: 0,
    activePurchases: 0,
    totalSales: 0,
    memberDays: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        // Count total listings
        const listingsQuery = query(
          collection(db, 'listings'),
          where('sellerId', '==', userId)
        );
        const listingsSnapshot = await getDocs(listingsQuery);
        const totalListings = listingsSnapshot.size;

        // Count purchases (as buyer)
        const purchasesQuery = query(
          collection(db, 'transactions'),
          where('buyerId', '==', userId)
        );
        const purchasesSnapshot = await getDocs(purchasesQuery);
        const activePurchases = purchasesSnapshot.size;

        // Count sales (as seller)
        const salesQuery = query(
          collection(db, 'transactions'),
          where('sellerId', '==', userId)
        );
        const salesSnapshot = await getDocs(salesQuery);
        const totalSales = salesSnapshot.size;

        // Calculate member days
        const joinedAt = userData.joinedAt?.toDate?.() || new Date();
        const memberDays = Math.floor((new Date() - joinedAt) / (1000 * 60 * 60 * 24));

        setStats({
          totalListings,
          activePurchases,
          totalSales,
          memberDays: memberDays || 0
        });
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [userData]);

  const statsData = [
    {
      icon: <PackageIcon />,
      label: 'Active Listings',
      value: stats.totalListings,
      color: COLORS.primary
    },
    {
      icon: <ShoppingBagIcon />,
      label: 'Purchases',
      value: stats.activePurchases,
      color: '#9d0d1d' // Darker ISU red
    },
    {
      icon: <DollarIcon />,
      label: 'Total Sales',
      value: stats.totalSales,
      color: '#b01121' // Medium ISU red
    },
    {
      icon: <StarIcon />,
      label: 'Member Days',
      value: stats.memberDays,
      color: '#d61c2e' // Lighter ISU red
    }
  ];

  const statsStyles = {
    container: {
      background: '#fff',
      borderRadius: 16,
      padding: LAYOUT.spacing.xl,
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
      marginBottom: LAYOUT.spacing.lg
    },
    
    header: {
      fontSize: 20,
      fontWeight: 700,
      color: COLORS.primary,
      marginBottom: LAYOUT.spacing.lg
    },
    
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: LAYOUT.spacing.md
    },
    
    statCard: {
      padding: LAYOUT.spacing.md,
      borderRadius: 12,
      background: '#f8f9fa',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: LAYOUT.spacing.sm,
      transition: 'all 0.3s ease'
    },
    
    iconWrapper: (color) => ({
      width: 48,
      height: 48,
      borderRadius: 12,
      background: `${color}15`,
      color: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }),
    
    value: {
      fontSize: 28,
      fontWeight: 700,
      color: COLORS.text.primary,
      margin: 0
    },
    
    label: {
      fontSize: 13,
      color: COLORS.text.secondary,
      fontWeight: 500,
      textAlign: 'center'
    },
    
    loadingText: {
      textAlign: 'center',
      color: COLORS.text.secondary,
      padding: LAYOUT.spacing.lg
    }
  };

  if (loading) {
    return (
      <div style={statsStyles.container}>
        <div style={statsStyles.header}>Profile Statistics</div>
        <div style={statsStyles.loadingText}>Loading stats...</div>
      </div>
    );
  }

  return (
    <div style={statsStyles.container}>
      <div style={statsStyles.header}>Profile Statistics</div>
      <div style={statsStyles.grid}>
        {statsData.map((stat, index) => (
          <div
            key={index}
            style={statsStyles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={statsStyles.iconWrapper(stat.color)}>
              {stat.icon}
            </div>
            <div style={statsStyles.value}>{stat.value}</div>
            <div style={statsStyles.label}>{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileStats;
